import Flex from 'legacyfrontend/components/Flex';
import paquetesApi from 'legacyfrontend/api/paquetes';
import { FALLBACK_IMAGE, DATE_KEYS } from 'legacyfrontend/constants';
import { usePackageCard } from 'legacyfrontend/contexts/PackageCardContext';
import getFinalPrice from 'legacyfrontend/utils/get-final-price';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import { useTranslation } from 'react-i18next';
import { formatWithMomentFormat, utcToLocal } from 'legacyfrontend/utils/dateUtils';
import useCurrentLanguage from 'legacyfrontend/hooks/useCurrentLanguage';
import CardGallery from './components/CardGallery';
import DynamicContent from './components/DynamicContent';
import DynamicIncludesRow from './DynamicIncludesRow';
import totalNightTextSetter from 'legacyfrontend/utils/total-night-text';
import { selectApplicationState, selectToken } from 'legacyfrontend/redux/selectors/application-selectors';
import { useSelector } from 'react-redux';
import { saveSearchParameters, extractRelevantSearchParams } from 'legacyfrontend/helpers/searchParametersStorage';

const PackageCard = () => {
  const { t } = useTranslation(['layout']);
  const urlParams = new URLSearchParams(isClient ? window.location.search : '');
  const currentLanguage = useCurrentLanguage();
  const [showGallery, setShowGallery] = useState();
  const [availableDates, setAvailableDates] = useState([]);
  const [dateSince, setDateSince] = useState(null);
  const [loadingSalidas, setLoadingSalidas] = useState(false);
  const [hasAvailableSalidas, setHasAvailableSalidas] = useState(false);
  const { packageData, displayInfo, handleDisplayInfoToggle } = usePackageCard();
  const { web } = useSelector(selectApplicationState);
  const token = useSelector(selectToken);

  const isInternal = useMemo(() => packageData?.isInternal, [packageData]);
  const url = web === 'localhost' ? `http://localhost:${process.env.PORT}` : `https://${web}`;

  const selectedMonth = urlParams?.get('monthYear') ? parseInt(urlParams.get('monthYear').split('-')[0]) : null;
  const selectedYear = urlParams?.get('monthYear') ? parseInt(urlParams.get('monthYear').split('-')[1]) : null;

  const monthYear = useMemo(() => {
    if (!selectedMonth || !selectedYear) return null;
    return `${selectedMonth.toString().padStart(2, '0')}-${selectedYear}`;
  }, [selectedMonth, selectedYear]);

  const adultsQty = useMemo(() => {
    const qty = urlParams?.get('adultsQty');
    return qty ? parseInt(qty, 10) : null;
  }, [urlParams]);

  const handlePackageSalidas = useCallback(async () => {
    if (!monthYear || !packageData?.externalId || !token) return;

    try {
      setLoadingSalidas(true);
      setHasAvailableSalidas(false);
      setAvailableDates([]);

      const response = await paquetesApi.getInternalPackageSalidas(packageData.externalId, monthYear, token, adultsQty);

      if (!response || response.length === 0) {
        setAvailableDates([]);
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let filteredSalidas = response.filter((salida) => {
        if (!salida.dateSince) return false;

        const salidaDate = new Date(salida.dateSince);
        const salidaDateUTC = new Date(
          Date.UTC(
            salidaDate.getUTCFullYear(),
            salidaDate.getUTCMonth(),
            salidaDate.getUTCDate()
          )
        );

        const todayUTC = new Date(
          Date.UTC(
            today.getUTCFullYear(),
            today.getUTCMonth(),
            today.getUTCDate()
          )
        );

        return salidaDateUTC >= todayUTC;
      });

      const [month, year] = monthYear.split('-').map(Number);

      filteredSalidas = filteredSalidas.map((salida) => {
        if (!salida.dateSince) return salida;

        const salidaDate = new Date(salida.dateSince);
        const salidaMonth = salidaDate.getUTCMonth();
        const salidaYear = salidaDate.getUTCFullYear();

        if (salidaMonth < month - 1 || (salidaMonth === month - 2 && salidaYear < year)) {
          const adjustedDate = new Date(Date.UTC(year, month - 1, 1));
          return {
            ...salida,
            dateSince: adjustedDate.toISOString(),
          };
        }

        return salida;
      });

      setAvailableDates(filteredSalidas);
      setHasAvailableSalidas(filteredSalidas.length > 0);
    } catch (error) {
      console.error(`[PackageCard] Error al obtener las salidas: ${error?.response?.data}`, error);
      setAvailableDates([]);
      setHasAvailableSalidas(false);
    } finally {
      setLoadingSalidas(false);
    }
  }, [packageData?.externalId, monthYear, token, adultsQty]);

  const {
    title,
    images,
    totalNights,
    packageOrigins,
    packageDestinations,
    currencyCode,
    cheapestOption,
  } = packageData;

  const departureDate = useMemo(() => {
    const tripInformation = cheapestOption?.flight?.tripInformation;
    if (tripInformation && tripInformation.length > 0) {
      const sortedTripInfo = [...tripInformation].sort((a, b) => a.id - b.id);
      const flightDepartureDate = sortedTripInfo[0]?.departureDate;
      if (flightDepartureDate) {
        const dateFormats = {
          es: 'D [de] MMMM [de] YYYY',
          en: 'MMMM D, YYYY',
          pt: 'D [de] MMMM [de] YYYY',
        };

        const format = dateFormats[currentLanguage] || dateFormats.es;
        return formatWithMomentFormat(utcToLocal(flightDepartureDate), format);
      }
    }
    return null;
  }, [cheapestOption, currentLanguage]);

  const galleyOfImages = useMemo(() => {
    if (images && images.length > 0) {
      return images;
    }

    if (packageDestinations && packageDestinations.length > 0) {
      const destinationImages = packageDestinations
        .map((dest) => dest.destinationCity?.imageUrl)
        .filter((imageUrl) => imageUrl && imageUrl.trim() !== '');

      if (destinationImages.length > 0) {
        return destinationImages;
      }
    }

    return [FALLBACK_IMAGE];
  }, [images, packageDestinations]);

  const mainImage = useMemo(() => {
    if (images && images.length > 0) {
      return images[0];
    }

    if (packageDestinations && packageDestinations.length > 0) {
      const firstDestinationImage = packageDestinations[0]?.destinationCity?.imageUrl;
      if (firstDestinationImage && firstDestinationImage.trim() !== '') {
        return firstDestinationImage;
      }
    }

    return FALLBACK_IMAGE;
  }, [images, packageDestinations]);

  const handleShowGallery = () => {
    setShowGallery((state) => !state);
  };

  const handleClick = useCallback(() => {
    if (isClient && hasAvailableSalidas && availableDates.length > 0) {
      const firstSalida = availableDates[0];
      const currentSearch = new URLSearchParams(window.location.search);
      const params = new URLSearchParams();

      params.set('id', String(packageData.externalId ?? packageData.id));

      if (firstSalida?.salidaId != null) {
        params.set('salidaId', String(firstSalida.salidaId));
      }

      const monthYearValue = currentSearch.get('monthYear') || monthYear;
      if (monthYearValue) {
        params.set('monthYear', monthYearValue);
      }

      const adultsQtyFromUrl = currentSearch.get('adultsQty');
      if (adultsQtyFromUrl) {
        params.set('adultsQty', adultsQtyFromUrl);
      } else if (adultsQty) {
        params.set('adultsQty', adultsQty.toString());
      }

      const providerTypeValue =
        packageData?.provider?.providerType || currentSearch.get('providerType');
      if (providerTypeValue) {
        params.set('providerType', String(providerTypeValue));
      }

      const relevantParams = extractRelevantSearchParams(
        `?${currentSearch.toString()}`,
        packageData,
        firstSalida?.salidaId,
        true
      );
      saveSearchParameters(relevantParams);

      const urlSalidas = `${url}/paquetes?${params.toString()}`;
      window.open(urlSalidas, '_blank');
    }
  }, [url, packageData, availableDates, monthYear, adultsQty, hasAvailableSalidas]);

  useEffect(() => {
    if (isInternal) {
      handlePackageSalidas();
    }
  }, [isInternal, handlePackageSalidas]);

  useEffect(() => {
    setAvailableDates([]);
    setDateSince(null);
  }, [monthYear, adultsQty, packageData?.externalId]);

  const salidaDateDisplay = useMemo(() => {
    if (availableDates.length > 0) {
      const firstSalida = availableDates[0];
      if (firstSalida?.dateSince) {
        const sinceDate = new Date(firstSalida.dateSince);
        const sinceUTC = new Date(
          sinceDate.getUTCFullYear(),
          sinceDate.getUTCMonth(),
          sinceDate.getUTCDate()
        );
        return formatWithMomentFormat(sinceUTC, DATE_KEYS.FORMAT.DAY_SHORT);
      }
    }
    return null;
  }, [availableDates]);

  useEffect(() => {
    if (availableDates.length > 0 && !dateSince) {
      const firstDate = availableDates[0];
      const sinceDate = new Date(firstDate.dateSince);
      const sinceUTC = new Date(
        sinceDate.getUTCFullYear(),
        sinceDate.getUTCMonth(),
        sinceDate.getUTCDate()
      );
      const formattedDate = formatWithMomentFormat(sinceUTC, DATE_KEYS.FORMAT.NUMBER_MONTHNAME_YEAR);
      setDateSince(formattedDate);
    }
  }, [availableDates, dateSince]);

  return (
    <Flex vertical className='package-card'>
      <Flex className='package-card__data'>
        <img
          className='package-card__data__img'
          onClick={handleShowGallery}
          src={mainImage}
          alt={title || 'Paquete turístico'}
        />
        <Flex vertical className='package-card__data__wrapper'>
          <Flex vertical className='package-card__data__wrapper__info'>
            <h3 className='package-card__data__wrapper__info__title'>{title}</h3>
            <div className='package-card__data__wrapper__info__details'>
              <p>
                <strong>{t('packages.details.departure')}:</strong>
                <span>{packageOrigins[0].originExporta.nombreCiudad}</span>
              </p>
              <p>
                <strong>{t('searcher.destination')}:</strong>
                <span>{packageDestinations[0].destinationCity.nombreCiudad}</span>
              </p>
              <p>
                <strong>{totalNightTextSetter(totalNights)}</strong>
              </p>
              {isInternal && (salidaDateDisplay || dateSince) && (
                <p className='package-card__data__wrapper__info__details__departure'>
                  <strong>Salida:</strong>
                  <span>{salidaDateDisplay ?? dateSince}</span>
                </p>
              )}
              {!isInternal && departureDate && (
                <p className='package-card__data__wrapper__info__details__departure'>
                  <strong>Salida:</strong>
                  <span>{departureDate}</span>
                </p>
              )}
              <p className='package-card__data__wrapper__info__details__price'>
                <strong>{t('common.since')}:</strong>
                <span>
                  {getFinalPrice({
                    currencyCode,
                    total: cheapestOption.totalManager.total,
                  })}
                </span>
              </p>
            </div>
          </Flex>
          <Flex className='package-card__data__wrapper__footer'>
            {isInternal ? (
              <DynamicIncludesRow paquetesIncluidos={packageData?.icons} fullWidth />
            ) : (
              <DynamicIncludesRow paquetesIncluidos={[]} fullWidth />
            )}
            <Flex className='package-card__data__wrapper__footer__buttons'>
              {!isInternal && (
                <button
                  className={displayInfo ? 'active' : undefined}
                  onClick={handleDisplayInfoToggle}
                >
                  {t('common.more_details')}
                </button>
              )}
              {isInternal && (
                <button
                  onClick={handleClick}
                  disabled={loadingSalidas || !hasAvailableSalidas}
                >
                  {loadingSalidas ? 'Cargando...' : t('common.more_details')}
                </button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <DynamicContent />
      <CardGallery
        showGallery={showGallery}
        handleShowGallery={handleShowGallery}
        packageDestinations={packageDestinations}
        images={galleyOfImages}
      />
    </Flex>
  );
};

export default PackageCard;
